import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import NextLink from 'next/link';
import voca from 'voca';
import slug from 'slug';
import short from 'short-uuid';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import {
  Image,
  RESPONSVE_SIZES_CARDS,
  RESPONSVE_SIZES_CARDS_SMALL,
} from '@studionand/ada-next/src/components/Media/Image';
import PlusHighlighter from '../enplus/PlusHighlighter';
import formatDate from '../../shared/utils/formatDate';
import { CATEGORY_LABELS } from '../enplus/Unternehmen/constants';

const shortUuidTranslator = short();

moment.locale('de');

const useStyles = makeStyles(theme => ({
  inactivityBadge: {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translate(0, -50%)',
    background: theme.palette.primary.main,
    color: theme.palette.secondary.main,
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    letterSpacing: 4,
    padding: theme.spacing(0, 1),
  },
  disableLinks: {
    pointerEvents: 'none',
  },
  card: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    '&:hover $isPast': {
      opacity: 1,
    },
    '&:hover $djangoTitle': {
      color: theme.palette.primary.dark,
    },
    '&:hover $link': {
      color: theme.palette.primary.dark,
    },
    '&:hover': {
      borderLeftColor: theme.palette.primary.dark,
    },
  },
  inactive: {
    color: theme.palette.sketch.grey2,
    '&:hover $djangoTitle': {
      color: theme.palette.sketch.grey1,
    },
    '&:hover $link': {
      color: theme.palette.sketch.grey1,
    },
    '&$border': {
      borderLeftColor: theme.palette.sketch.grey2,
    },
    '&:hover': {
      '&$border': {
        borderLeftColor: theme.palette.sketch.grey1,
      },
    },
  },
  border: {
    borderLeftWidth: 5,
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette.primary.main,
  },
  pageContent: {
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 0,
    flexGrow: 1,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  contentLarge: {
    minHeight: 330,
  },
  smallContent: {
    minHeight: 200,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2),
    flexGrow: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
  },
  itemContent: {
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2),
    flexGrow: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
  },
  itemTitle: {
    marginRight: theme.spacing(1),
  },
  djangoContent: {
    minHeight: 275,
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  link: {
    color: 'inherit',
  },
  logo: {
    position: 'relative',
    // minWidth: 100,
    width: 100,
    height: 100,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right top',
    '&.depi': {
      maxWidth: 50,
      minWidth: 50,
    },
  },
  bottomContent: {
    marginTop: 'auto',
  },
  img: {
    position: 'relative',
    height: 150,
    width: '100%',
    backgroundColor: theme.palette.sketch.grey2,
    marginBottom: theme.spacing(1),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  imgLarge: {
    height: 225,
  },
  cell: {
    padding: theme.spacing(1),
    borderBottom: 'none',
    '&:first-child': {
      paddingLeft: 0,
      borderRight: `1px solid ${theme.palette.sketch.grey3}`,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },
  djangoTitle: {
    marginRight: theme.spacing(1),
  },
  isPast: {
    opacity: 0.5,
    transition: 'opacity 300ms ease-in-out',
  },
  eventContent: {
    minHeight: 230,
  },
  fileContent: {
    minHeight: 340,
  },
}));

const Link = ({ children, ...props }) => {
  return (
    <NextLink {...props}>
      <a>{children}</a>
    </NextLink>
  );
};

const getNotEmpty = t => (t && t.length > 0 ? t : null);

const enplusContext = ['hackschnitzel', 'pellets', 'holzbriketts'];

const getStand = props =>
  moment(props.startDate || props.updatedAt || props.createdAt).format('LL');

export const createLink = (context, uuid, title) => {
  const hash = shortUuidTranslator.fromUUID(uuid);
  const s = slug(title);

  if (enplusContext.indexOf(context) > -1) {
    const enplusLinkContext =
      process.env.NODE_ENV === 'production' ? '/' : `/enplus/${context}/`;
    return [
      `${enplusLinkContext}p/[uuid]`,
      `${enplusLinkContext}p/${s}-${hash}`,
    ];
  }
  const linkContext =
    process.env.NODE_ENV === 'production' ? '/' : `/${context}/`;
  return [`${linkContext}p/[uuid]`, `${linkContext}p/${s}-${hash}`];
};

const PageContent = props => {
  const classes = useStyles(props);
  const { data } = props;
  const abstract = data
    ? props.data.subtitle || props.data.abstract
    : props.subtitle || getNotEmpty(props.abstract);
  const link = createLink(props.context, props.uuid, props.title);
  return (
    <Link href={link[0]} as={link[1]}>
      <CardContent className={classes.pageContent}>
        <div className={classes.img}>
          <Image
            assetId={props.mediaId}
            layout="fill"
            objectFit="cover"
            sizes={RESPONSVE_SIZES_CARDS}
          />
        </div>
        <Box className={classes.content} minHeight={250}>
          <Typography variant="h6" color="primary" gutterBottom>
            <b>
              <PlusHighlighter textToHighlight={props.title} />
            </b>
          </Typography>
          {abstract && (
            <Typography variant="body2" gutterBottom>
              <PlusHighlighter
                textToHighlight={voca.prune(abstract, 120, '...')}
              />
            </Typography>
          )}
          <div className={classes.bottomContent}>
            <Typography variant="caption">Stand: {getStand(props)}</Typography>
          </div>
        </Box>
      </CardContent>
    </Link>
  );
};

const BotschafterContent = props => {
  const classes = useStyles(props);
  const { data } = props;
  const link = createLink(props.context, props.uuid, props.title);

  return (
    <Link href={link[0]} as={link[1]}>
      <CardContent className={cx(classes.pageContent, classes.smallContent)}>
        <div
          className={classes.img}
          style={{
            backgroundImage: `url(/ada-assets/${props.mediaId}?w=300&h=300)`,
          }}
        />
        <Box className={classes.content} minHeight={150}>
          <Typography variant="h6" color="primary" gutterBottom>
            <b>
              <PlusHighlighter textToHighlight={props.title} />
            </b>
          </Typography>
          <div className={classes.bottomContent}>
            <Typography variant="caption">Stand: {getStand(props)}</Typography>
          </div>
        </Box>
      </CardContent>
    </Link>
  );
};

const FileContent = props => {
  const classes = useStyles(props);
  const hasData = props.data;
  const mediaId =
    props.mediaId ||
    props.previewImage ||
    (hasData && (props.data.thumbnail || props.data.previewImage));

  const link = createLink(props.context, props.uuid, props.title);
  return (
    <Link href={link[0]} as={link[1]}>
      <CardContent className={cx(classes.pageContent, classes.fileContent)}>
        <div className={classes.img}>
          <Image
            assetId={mediaId}
            sizes={RESPONSVE_SIZES_CARDS}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div className={classes.content}>
          <Typography variant="h6" color="primary" gutterBottom>
            <b>
              <PlusHighlighter textToHighlight={props.title} />
            </b>
          </Typography>
          <div className={classes.bottomContent}>
            <Typography variant="caption">Stand: {getStand(props)}</Typography>
          </div>
        </div>
      </CardContent>
    </Link>
  );
};

const BildContent = props => {
  const classes = useStyles(props);
  const hasData = props.data;
  const mediaId =
    props.mediaId ||
    props.previewImage ||
    (hasData && (props.data.thumbnail || props.data.previewImage));
  const link = createLink(props.context, props.uuid, props.title);
  return (
    <Link href={link[0]} as={link[1]}>
      <CardContent className={cx(classes.pageContent, classes.contentLarge)}>
        <div className={cx(classes.img, classes.imgLarge)}>
          <Image
            assetId={mediaId}
            sizes={RESPONSVE_SIZES_CARDS}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div className={classes.content}>
          <Typography variant="body1" color="primary" gutterBottom>
            <b>
              <PlusHighlighter textToHighlight={voca.prune(props.title, 50)} />
            </b>
          </Typography>
          {null && (
            <div className={classes.bottomContent}>
              <Typography variant="caption">
                Stand: {getStand(props)}
              </Typography>
            </div>
          )}
        </div>
      </CardContent>
    </Link>
  );
};

const ITEM_TYPES = ['unternehmen', 'event', 'django-unternehmen'];
const prepareWebsiteLink = url => {
  if (url.startsWith('http:')) return url.trim();
  return `http://${url.trim()}`;
};

const ItemContent = props => {
  const classes = useStyles(props);
  const hasData = props.data;
  const address = props.address || (hasData && props.data.address);
  const link = createLink(props.context, props.uuid, props.title);
  return (
    <Link href={link[0]} as={link[1]} prefetch={false}>
      <CardContent className={classes.itemContent}>
        <div className={classes.itemHeader}>
          <Typography
            className={classes.itemTitle}
            variant="body1"
            color="primary"
            component="div"
          >
            <b>
              <PlusHighlighter textToHighlight={props.title} />
            </b>
            {props.website && (
              <Box mt={2}>
                <Typography color="primary">
                  <a
                    className={classes.link}
                    href={prepareWebsiteLink(props.website)}
                    target="_blank"
                    rel="noopener noreferer noreferrer"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    Zur Webseite
                  </a>
                </Typography>
              </Box>
            )}
          </Typography>
          <div
            className={classes.logo}
            style={{
              backgroundImage: `url(/ada-assets/${props.mediaId}?w=300&h=300)`,
            }}
          />
        </div>
        <div className={classes.bottomContent}>
          <Table>
            <TableBody>
              {address && (
                <TableRow>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Hauptsitz
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1">{address}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Link>
  );
};

const getCompanyLinkContext = context => {
  if (process.env.NODE_ENV === 'production') return '/';
  if (['depi', 'depv'].indexOf(context) > -1) return `/${context}/`;
  return `/enplus/${context}/`;
};

const DjangoContent = props => {
  const classes = useStyles(props);
  const productIds = props.productIds(props.context);
  const enplusLinkContext = getCompanyLinkContext(props.context);
  const isInactive = props.status === 'inactive';
  const isDepiOrDepvContext = ['depi', 'depv'].indexOf(props.context) > -1;
  return (
    <Link
      href={`${enplusLinkContext}unternehmen/[uuid]`}
      as={`${enplusLinkContext}unternehmen/${props.uuid}`}
      prefetch={false}
    >
      <CardContent className={cx(classes.itemContent, classes.djangoContent)}>
        {isInactive && (
          <Typography variant="subtitle2" className={classes.inactivityBadge}>
            inaktiv
          </Typography>
        )}
        <div className={classes.itemHeader}>
          <div>
            <Typography
              className={classes.djangoTitle}
              variant="body1"
              color={isInactive ? 'currentColor' : 'primary'}
              component="div"
            >
              <b>
                <PlusHighlighter textToHighlight={props.title} />
              </b>
            </Typography>
            {props.website && (
              <Box mt={2}>
                <Typography
                  color={isInactive ? 'currentColor' : 'primary'}
                  component="div"
                >
                  <a
                    className={classes.link}
                    href={`${props.website}`}
                    target="_blank"
                    rel="noopener noreferer noreferrer"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    Zur Webseite
                  </a>
                </Typography>
              </Box>
            )}
          </div>
          {props.mediaId && (
            <div className={classes.logo}>
              <Image
                pathPrefix="django-assets"
                assetId={props.mediaId?.replace('/assets', '')}
                layout="fill"
                objectFit="contain"
                objectPosition="top"
                sizes={RESPONSVE_SIZES_CARDS_SMALL}
              />
            </div>
          )}
        </div>
        <div className={classes.bottomContent}>
          <Table>
            <TableBody>
              {productIds && (
                <TableRow>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1" color="textSecondary">
                      ID
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography
                      variant="subtitle1"
                      color={isInactive ? 'textSecondary' : 'textPrimary'}
                    >
                      {productIds}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isDepiOrDepvContext && props.productNames && (
                <TableRow>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Produkt
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography
                      variant="subtitle1"
                      color={isInactive ? 'textSecondary' : 'textPrimary'}
                    >
                      {props.productNames}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {props.address && (
                <TableRow>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Hauptsitz
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography
                      variant="subtitle1"
                      color={isInactive ? 'textSecondary' : 'textPrimary'}
                    >
                      {props.address.street}, {props.address.country}-
                      {props.address.zip_code} {props.address.city}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Link>
  );
};

const createRangeLabel = (start, end) => {
  const startDay = start.format('DD');
  const endDay = end.format('DD');
  const startMonth = start.format('MM');
  const endMonth = end.format('MM');
  const startYear = start.format('YYYY');
  const endYear = start.format('YYYY');
  if (startMonth !== endMonth) {
    if (startYear !== endYear) {
      return `${startDay}.${startMonth}.${startYear} - ${endDay}.${endMonth}.${endYear}`;
    }
    return `${startDay}.${startMonth} - ${endDay}.${endMonth}.${endYear}`;
  }
  return `${startDay} - ${endDay}.${endMonth}.${endYear}`;
};

const EventContent = props => {
  const { startDate, endDate, context } = props;
  const classes = useStyles(props);
  const date = formatDate({
    startDate: moment(startDate),
    endDate: endDate && moment(endDate),
    prefix: '',
    fallback: '',
  });
  const isPast = moment(endDate || startDate).isBefore(moment().startOf('day'));
  const hasData = props.data;
  const place = props.place || (hasData && props.data.place);
  const link = createLink(props.context, props.uuid, props.title);

  return (
    <Link href={link[0]} as={link[1]}>
      <CardContent
        className={cx(classes.itemContent, classes.eventContent, {
          [classes.isPast]: isPast,
        })}
      >
        <div className={classes.itemHeader}>
          <Typography className={classes.itemTitle} variant="h6">
            <b>{date}</b>
            <Typography color="primary">
              <b>
                <PlusHighlighter textToHighlight={props.title} />
              </b>
            </Typography>
          </Typography>
          <div className={cx(classes.logo, context)}>
            <Image
              assetId={props.mediaId}
              layout="fill"
              objectFit="contain"
              objectPosition="top"
              sizes="20vw"
            />
          </div>
        </div>
        <div className={classes.bottomContent}>
          <Table>
            <TableBody>
              {props.address && (
                <TableRow>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1" color="textSecondary">
                      Hauptsitz
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography variant="subtitle1">{props.address}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {place && (
            <Box mt={1}>
              <Typography variant="caption">{place}</Typography>
            </Box>
          )}
        </div>
      </CardContent>
    </Link>
  );
};

const ResultCard = props => {
  const { data, context, disableLinks } = props;
  const [mouseOver, setMouseOver] = React.useState(false);
  const classes = useStyles(props);
  const hasBorder = !ITEM_TYPES.includes(data.type);
  const Content = () => {
    switch (data.type) {
      case 'django-unternehmen':
        return <DjangoContent {...data} context={context} />;
      case 'unternehmen':
        return <ItemContent {...data} context={context} />;
      case 'event':
        return <EventContent {...data} context={context} />;
      case 'datei':
        return <FileContent {...data} context={context} />;
      case 'bild':
        return <BildContent {...data} context={context} />;
      case 'botschafter':
        return <BotschafterContent {...data} context={context} />;
      default:
        return <PageContent {...data} context={context} />;
    }
  };
  return (
    <Card
      className={cx(classes.card, {
        [classes.border]: !hasBorder,
        [classes.disableLinks]: disableLinks,
        [classes.inactive]: data.status === 'inactive',
      })}
      raised={mouseOver}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      {Content()}
    </Card>
  );
};

ResultCard.defaultProps = {
  disableLinks: false,
};

ResultCard.propTypes = {
  data: PropTypes.object.isRequired,
  context: PropTypes.string.isRequired,
  disableLinks: PropTypes.bool,
};

export default ResultCard;
